import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatDuration,
  formatNumber,
  toNumber
} from './panel-utils.js';

const DEFAULT_SESSION = {
  name: 'Strength Block',
  date: '2026-01-16',
  athlete: 'Operator V'
};

const DEFAULT_EXERCISES = [
  {
    id: 'back-squat',
    name: 'Back Squat',
    rest: 120,
    notes: 'Tempo 3-1-1',
    sets: [
      { id: 'sq-1', weight: 140, reps: 5, rpe: 7, status: 'done' },
      { id: 'sq-2', weight: 145, reps: 5, rpe: 7.5, status: 'done' },
      { id: 'sq-3', weight: 150, reps: 4, rpe: 8.5, status: 'pending' },
      { id: 'sq-4', weight: 150, reps: 4, rpe: 9, status: 'pending' }
    ]
  },
  {
    id: 'bench-press',
    name: 'Bench Press',
    rest: 90,
    notes: 'Pause 1s on chest',
    sets: [
      { id: 'bp-1', weight: 95, reps: 6, rpe: 7, status: 'done' },
      { id: 'bp-2', weight: 97.5, reps: 6, rpe: 7.5, status: 'pending' },
      { id: 'bp-3', weight: 97.5, reps: 5, rpe: 8, status: 'pending' }
    ]
  },
  {
    id: 'pull-up',
    name: 'Weighted Pull-up',
    rest: 75,
    notes: 'Full hang',
    sets: [
      { id: 'pu-1', weight: 10, reps: 8, rpe: 7, status: 'done' },
      { id: 'pu-2', weight: 12.5, reps: 7, rpe: 8, status: 'pending' },
      { id: 'pu-3', weight: 12.5, reps: 6, rpe: 8.5, status: 'pending' }
    ]
  }
];

const STATUS_FLOW = ['pending', 'done', 'missed'];

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Lift Set Tracker');
  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';
  let currentNode = node;

  const layout = document.createElement('div');
  layout.className = 'ew-lift-layout';

  const list = document.createElement('div');
  list.className = 'ew-list';

  const detail = document.createElement('div');
  detail.className = 'ew-detail ew-lift-detail';

  layout.append(list, detail);
  body.append(metrics, layout);

  const update = (next) => {
    currentNode = next;
    titleEl.textContent = resolveTitle(next, 'Lift Set Tracker');
    const unit = resolveUnit(next);
    const session = resolveSession(next);
    const exercises = resolveExercises(next);
    const allSets = exercises.flatMap((exercise) => Array.isArray(exercise.sets) ? exercise.sets : []);
    const doneSets = allSets.filter((set) => set.status === 'done');
    const volumeSets = doneSets.length ? doneSets : allSets;
    const volume = volumeSets.reduce((sum, set) => sum + toNumber(set.weight, 0) * toNumber(set.reps, 0), 0);
    const avgRpe = volumeSets.length
      ? volumeSets.reduce((sum, set) => sum + toNumber(set.rpe, 0), 0) / volumeSets.length
      : 0;

    metaEl.textContent = [
      session?.name,
      session?.date,
      session?.athlete
    ].filter(Boolean).join(' | ');

    metrics.innerHTML = '';
    metrics.append(
      createMetric('sets', `${doneSets.length}/${allSets.length}`),
      createMetric('volume', `${formatNumber(volume, 0)} ${unit}`),
      createMetric('avg rpe', avgRpe ? avgRpe.toFixed(1) : '--')
    );

    list.innerHTML = '';
    exercises.forEach((exercise) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ew-list-item';
      if (exercise.id === (next?.data?.selectedExercise || exercises[0]?.id)) {
        button.classList.add('is-active');
      }

      const head = document.createElement('div');
      head.className = 'ew-list-head';
      const name = document.createElement('span');
      name.className = 'ew-list-title';
      name.textContent = exercise.name || exercise.id;
      const tag = document.createElement('span');
      tag.className = 'ew-tag';
      const sets = Array.isArray(exercise.sets) ? exercise.sets : [];
      const done = sets.filter((set) => set.status === 'done').length;
      tag.textContent = `${done}/${sets.length} sets`;
      head.append(name, tag);

      const meta = document.createElement('div');
      meta.className = 'ew-list-meta';
      const last = findLastLoggedSet(sets) || sets[sets.length - 1];
      if (last) {
        const weightText = formatWeight(last.weight);
        const repsText = formatReps(last.reps);
        meta.textContent = `Last ${weightText}${unit} x ${repsText}`;
      } else {
        meta.textContent = `Rest ${formatDuration(toNumber(exercise.rest, 0))}`;
      }

      button.append(head, meta);
      button.addEventListener('click', () => {
        api.setData({ selectedExercise: exercise.id });
        api.emit('node:input', { id: node.id, field: 'exercise', value: exercise.id });
      });
      list.appendChild(button);
    });

    detail.innerHTML = '';
    const selectedId = next?.data?.selectedExercise || exercises[0]?.id;
    const selected = exercises.find((exercise) => exercise.id === selectedId) || exercises[0];
    if (!selected) {
      const empty = document.createElement('div');
      empty.className = 'ew-html-fallback';
      empty.textContent = 'No exercises';
      detail.appendChild(empty);
      return;
    }

    const summary = document.createElement('div');
    summary.className = 'ew-lift-summary';
    const summaryTitle = document.createElement('div');
    summaryTitle.className = 'ew-lift-name';
    summaryTitle.textContent = selected.name || selected.id;
    const summaryMeta = document.createElement('div');
    summaryMeta.className = 'ew-lift-meta';
    summaryMeta.textContent = [
      selected.notes,
      selected.rest ? `Rest ${formatDuration(toNumber(selected.rest, 0))}` : null
    ].filter(Boolean).join(' | ');

    const progress = document.createElement('div');
    progress.className = 'ew-progress';
    const progressBar = document.createElement('span');
    progressBar.className = 'ew-progress-bar';
    const selectedSets = Array.isArray(selected.sets) ? selected.sets : [];
    const doneCount = selectedSets.filter((set) => set.status === 'done').length;
    const pct = selectedSets.length ? (doneCount / selectedSets.length) * 100 : 0;
    progressBar.style.width = `${pct}%`;
    progress.appendChild(progressBar);

    summary.append(summaryTitle, summaryMeta, progress);

    const setsWrap = document.createElement('div');
    setsWrap.className = 'ew-lift-sets';
    selectedSets.forEach((set, index) => {
      const card = document.createElement('div');
      const status = normalizeStatus(set.status);
      card.className = `ew-lift-set is-${status}`;

      const header = document.createElement('div');
      header.className = 'ew-lift-set-header';
      const label = document.createElement('span');
      label.textContent = `Set ${index + 1}`;
      const statusBtn = document.createElement('button');
      statusBtn.type = 'button';
      statusBtn.className = `ew-btn-mini ew-lift-status is-${status}`;
      statusBtn.textContent = status.toUpperCase();
      statusBtn.addEventListener('click', () => {
        const nextStatus = cycleStatus(status);
        updateSet(api, currentNode, selected.id, set.id, { status: nextStatus });
        api.emit('node:input', { id: node.id, field: 'status', value: nextStatus, setId: set.id, exerciseId: selected.id });
      });
      header.append(label, statusBtn);

      const fields = document.createElement('div');
      fields.className = 'ew-lift-set-inputs';
      const weightInput = createNumberInput(set.weight, 0.5);
      const repsInput = createNumberInput(set.reps, 1);
      const rpeInput = createNumberInput(set.rpe, 0.5, 6, 10);

      weightInput.addEventListener('change', () => {
        updateSet(api, currentNode, selected.id, set.id, { weight: parseNumber(weightInput.value) });
        api.emit('node:input', { id: node.id, field: 'weight', value: weightInput.value, setId: set.id, exerciseId: selected.id });
      });
      repsInput.addEventListener('change', () => {
        updateSet(api, currentNode, selected.id, set.id, { reps: parseNumber(repsInput.value) });
        api.emit('node:input', { id: node.id, field: 'reps', value: repsInput.value, setId: set.id, exerciseId: selected.id });
      });
      rpeInput.addEventListener('change', () => {
        updateSet(api, currentNode, selected.id, set.id, { rpe: parseNumber(rpeInput.value) });
        api.emit('node:input', { id: node.id, field: 'rpe', value: rpeInput.value, setId: set.id, exerciseId: selected.id });
      });

      fields.append(
        createField(`Weight (${unit})`, weightInput),
        createField('Reps', repsInput),
        createField('RPE', rpeInput)
      );

      card.append(header, fields);
      setsWrap.appendChild(card);
    });

    const addSet = document.createElement('button');
    addSet.type = 'button';
    addSet.className = 'ew-btn-mini ew-lift-add';
    addSet.textContent = 'Add set';
    addSet.addEventListener('click', () => {
      addNewSet(api, currentNode, selected.id);
    });

    detail.append(summary, setsWrap, addSet);
  };

  update(node);
  return { el: root, update };
}

function resolveExercises(node) {
  const list = node?.data?.exercises ?? node?.props?.exercises;
  return Array.isArray(list) ? list : DEFAULT_EXERCISES;
}

function resolveSession(node) {
  return node?.data?.session || node?.props?.session || DEFAULT_SESSION;
}

function resolveUnit(node) {
  return node?.data?.unit || node?.props?.unit || 'kg';
}

function normalizeStatus(status) {
  const value = typeof status === 'string' ? status.toLowerCase() : status;
  return STATUS_FLOW.includes(value) ? value : 'pending';
}

function cycleStatus(current) {
  const idx = STATUS_FLOW.indexOf(normalizeStatus(current));
  return STATUS_FLOW[(idx + 1) % STATUS_FLOW.length];
}

function createNumberInput(value, step, min, max) {
  const input = document.createElement('input');
  input.type = 'number';
  input.step = String(step || 1);
  if (min !== undefined) input.min = String(min);
  if (max !== undefined) input.max = String(max);
  input.value = value === null || value === undefined ? '' : String(value);
  return input;
}

function createField(labelText, input) {
  const field = document.createElement('label');
  field.className = 'ew-lift-field';
  const label = document.createElement('span');
  label.textContent = labelText;
  field.append(label, input);
  return field;
}

function parseNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatWeight(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '--';
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1);
}

function formatReps(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '--';
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(1);
}

function updateSet(api, currentNode, exerciseId, setId, patch) {
  const exercises = resolveExercises(currentNode).map((exercise) => {
    if (exercise.id !== exerciseId) return exercise;
    const sets = Array.isArray(exercise.sets) ? exercise.sets : [];
    return {
      ...exercise,
      sets: sets.map((set) => (set.id === setId ? { ...set, ...patch } : set))
    };
  });
  api.setData({ exercises });
}

function addNewSet(api, currentNode, exerciseId) {
  const exercises = resolveExercises(currentNode).map((exercise) => {
    if (exercise.id !== exerciseId) return exercise;
    const sets = Array.isArray(exercise.sets) ? exercise.sets : [];
    const last = sets[sets.length - 1] || {};
    const next = {
      id: `set-${Date.now().toString(36)}`,
      weight: last.weight ?? null,
      reps: last.reps ?? null,
      rpe: last.rpe ?? null,
      status: 'pending'
    };
    return { ...exercise, sets: [...sets, next] };
  });
  api.setData({ exercises });
}

function findLastLoggedSet(sets) {
  if (!Array.isArray(sets)) return null;
  for (let i = sets.length - 1; i >= 0; i -= 1) {
    const set = sets[i];
    if (!set) continue;
    if (Number.isFinite(Number(set.weight)) || Number.isFinite(Number(set.reps))) {
      return set;
    }
  }
  return null;
}
